/**
 * 忘记密码视图（任务 6e；protocol §3.5）：*_find_password 验证码（防水墙同登录发码）
 * → 新密码两次输入 → find_password。成功回登录表单（密码 Tab）并提示用新密码登录。
 * 与登录发码共用 classifyCodeAccount / CaptchaDialog / 倒计时口径。
 */
import { useState } from 'preact/hooks';
import styles from './login.module.scss';
import { CaptchaDialog } from './CaptchaDialog';
import type { CaptchaResult } from './captcha';
import { COUNTRY_CODES } from './countryCodes';
import { useCountdown } from './useCountdown';
import { appWorker } from '~/store/appWorker';
import {
  classifyCodeAccount,
  loginErrorMessage,
  resetPassword,
  sendLoginCode,
} from '~/store/authActions';

export function ForgotPasswordView(props: {
  onDone: (notice: string) => void;
  onBack: () => void;
}) {
  const [account, setAccount] = useState('');
  const [countryCode, setCountryCode] = useState(86);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const countdown = useCountdown();
  /** 非空 = 防水墙弹层打开（值 = 解密后的 AppId） */
  const [captchaAppId, setCaptchaAppId] = useState('');

  const isEmail = account.includes('@');

  async function requestCode() {
    if (busy || countdown.seconds > 0) return;
    const target = classifyCodeAccount(account, countryCode);
    if (target.kind === 'invalid') {
      setError(target.message);
      return;
    }
    setError('');
    setBusy(true);
    try {
      const config = await appWorker().call('config/get', null);
      setCaptchaAppId(config.captchaAppId);
    } catch (err) {
      setError(loginErrorMessage(err, '验证码发送失败，请稍后重试'));
    } finally {
      setBusy(false);
    }
  }

  async function onCaptchaDone(result: CaptchaResult | undefined) {
    setCaptchaAppId('');
    if (result === undefined) return; // 用户取消：不弹错误（旧版口径）
    const target = classifyCodeAccount(account, countryCode);
    if (target.kind === 'invalid') return;
    setBusy(true);
    const sent = await sendLoginCode(appWorker(), target, result, 'find_password');
    setBusy(false);
    if (sent.ok) countdown.start(60);
    else setError(sent.message);
  }

  async function submit(event: Event) {
    event.preventDefault();
    if (busy) return;
    const target = classifyCodeAccount(account, countryCode);
    if (target.kind === 'invalid') {
      setError(target.message);
      return;
    }
    if (code.trim().length < 4) {
      setError('请输入验证码');
      return;
    }
    if (password === '') {
      setError('请输入新密码');
      return;
    }
    if (password !== passwordConfirm) {
      setError('两次密码输入不一致');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await resetPassword(appWorker(), target, code.trim(), password);
    setBusy(false);
    if (outcome.ok) props.onDone('密码已重置，请用新密码登录');
    else setError(outcome.message);
  }

  return (
    <form className={styles.form} data-testid="forgot-view" onSubmit={submit}>
      <h2 className={styles.viewTitle}>找回密码</h2>
      <div className={styles.row}>
        {!isEmail && (
          <select
            className={styles.countrySelect}
            data-testid="forgot-country-code"
            value={countryCode}
            onChange={(e) => setCountryCode(Number(e.currentTarget.value))}
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>
                +{c.code}
              </option>
            ))}
          </select>
        )}
        <input
          className={styles.input}
          data-testid="forgot-account"
          placeholder="手机号 / 邮箱"
          autocomplete="username"
          inputMode={isEmail ? 'email' : 'tel'}
          value={account}
          onInput={(e) => setAccount(e.currentTarget.value)}
        />
      </div>
      <div className={styles.row}>
        <input
          className={styles.input}
          data-testid="forgot-code"
          placeholder="验证码"
          autocomplete="one-time-code"
          inputMode="numeric"
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
        />
        <button
          type="button"
          className={styles.sendCode}
          data-testid="forgot-send-code"
          disabled={busy || countdown.seconds > 0 || account.trim() === ''}
          onClick={requestCode}
        >
          {countdown.seconds > 0 ? `${countdown.seconds}s` : '获取验证码'}
        </button>
      </div>
      <input
        className={styles.input}
        data-testid="forgot-password"
        type="password"
        placeholder="新密码"
        autocomplete="new-password"
        value={password}
        onInput={(e) => setPassword(e.currentTarget.value)}
      />
      <input
        className={styles.input}
        data-testid="forgot-password-confirm"
        type="password"
        placeholder="确认新密码"
        autocomplete="new-password"
        value={passwordConfirm}
        onInput={(e) => setPasswordConfirm(e.currentTarget.value)}
      />
      {error !== '' && (
        <p className={styles.error} data-testid="forgot-error">
          {error}
        </p>
      )}
      <button
        className={styles.submit}
        data-testid="forgot-submit"
        type="submit"
        disabled={busy || account.trim() === '' || code.trim() === '' || password === ''}
      >
        {busy ? '请稍候…' : '重置密码'}
      </button>
      <button type="button" className={styles.linkButton} data-testid="forgot-back" onClick={props.onBack}>
        返回登录
      </button>
      {captchaAppId !== '' && <CaptchaDialog appId={captchaAppId} onDone={onCaptchaDone} />}
    </form>
  );
}
